import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Dumbbell, Clock, Users, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getHomeBlurPlaceholder } from "@/utils/homeBlurPlaceholders";

const GymPoolSection = () => {
  // Safe hook usage with fallback
  let t: (key: string) => string;
  try {
    const { t: useLanguageT } = useLanguage();
    t = useLanguageT;
  } catch (error) {
    // Fallback to English translations if context is not available
    t = (key: string) => {
      const fallbackTranslations: Record<string, string> = {
        "gymPool.technogymTitle": "TECHNOGYM Fitness Center",
        "gymPool.technogymDesc":
          "Fully equipped with premium Technogym equipment",
        "gymPool.premiumEquipment": "Premium Equipment",
        "gymPool.premiumEquipmentDesc":
          "State-of-the-art Technogym machines for cardio, strength training, and functional fitness",
        "gymPool.personalTraining": "Personal Training",
        "gymPool.personalTrainingDesc":
          "Professional trainers available for personalized workout sessions",
        "gymPool.access24": "24/7 Access",
        "gymPool.access24Desc":
          "Round-the-clock access for guests to maintain their fitness routine",
        "gymPool.availableToGuests": "Available to all resort guests",
        "gymPool.bookYourStay": "Book Your Stay",
      };
      return fallbackTranslations[key] || key;
    };
  }

  return (
    <section
      className="pb-20 bg-gradient-to-b from-elegant-white via-pebble-beige to-elegant-white"
      data-oid="0jzbx-2"
    >
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        data-oid="e6jtjuf"
      >
        {/* Main Content Grid */}
        <div
          className="grid grid-cols-1 gap-12 items-start"
          data-oid="21wnpuj"
        >
          {/* Gym Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-8 max-w-2xl mx-auto"
            data-oid="i-cuk.:"
          >
            {/* Gym Image Placeholder */}
            <div
              className="relative h-80 rounded-3xl overflow-hidden shadow-2xl"
              data-oid="am50c-s"
            >
              <OptimizedImage
                src="/images/Homepage Shortlisted media/fitness centre horizontal.optimized.webp"
                alt="Technogym Fitness Center"
                className="w-full h-full object-cover"
                priority={false}
                fetchPriority="auto"
                blurDataURL={getHomeBlurPlaceholder("/images/Homepage Shortlisted media/fitness centre horizontal.optimized.webp")}
                data-oid=":6m1j0u"
              />

              <div
                className="absolute inset-0 bg-gradient-to-t from-pine-green/60 to-transparent"
                data-oid="la--5g2"
              ></div>
              <div className="absolute bottom-6 left-6" data-oid="u40g5b-">
                <h3
                  className="text-3xl text-white font-playfair mb-2"
                  data-oid="zr00zzy"
                >
                  {t("gymPool.technogymTitle")}
                </h3>
                <p className="text-white/90 font-lato" data-oid="f6.9ki4">
                  {t("gymPool.technogymDesc")}
                </p>
              </div>
            </div>

            {/* Gym Features */}
            <div className="space-y-6" data-oid="f-z4aq_">
              <div className="flex items-start gap-4" data-oid=".f_b0ym">
                <div
                  className="w-12 h-12 bg-alpine-gold rounded-full flex items-center justify-center flex-shrink-0"
                  data-oid="5v5oo6-"
                >
                  <Dumbbell className="w-6 h-6 text-white" data-oid="s3hd3ah" />
                </div>
                <div data-oid="skxlw15">
                  <h4
                    className="text-xl font-semibold text-gray-900 font-playfair mb-2"
                    data-oid="19irck7"
                  >
                    {t("gymPool.premiumEquipment")}
                  </h4>
                  <p className="text-gray-900 font-lato" data-oid="ft:g1t2">
                    {t("gymPool.premiumEquipmentDesc")}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-oid="jri2.qm">
                <div
                  className="w-12 h-12 bg-alpine-gold rounded-full flex items-center justify-center flex-shrink-0"
                  data-oid="qz5ygo1"
                >
                  <Users className="w-6 h-6 text-white" data-oid="eklkhyw" />
                </div>
                <div data-oid="k1v_3eb">
                  <h4
                    className="text-xl font-semibold text-gray-900 font-playfair mb-2"
                    data-oid="jobf56n"
                  >
                    {t("gymPool.personalTraining")}
                  </h4>
                  <p className="text-gray-900 font-lato" data-oid="x396xi.">
                    {t("gymPool.personalTrainingDesc")}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4" data-oid="pe17nji">
                <div
                  className="w-12 h-12 bg-alpine-gold rounded-full flex items-center justify-center flex-shrink-0"
                  data-oid="8wpqwcj"
                >
                  <Clock className="w-6 h-6 text-white" data-oid="5hlrdkj" />
                </div>
                <div data-oid="yo61fd3">
                  <h4
                    className="text-xl font-semibold text-gray-900 font-playfair mb-2"
                    data-oid="qx_l6cm"
                  >
                    {t("gymPool.access24")}
                  </h4>
                  <p className="text-gray-900 font-lato" data-oid="uldj5wh">
                    {t("gymPool.access24Desc")}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
          data-oid="ekjbll2"
        >
          <div
            className="flex items-center justify-center gap-2 mb-6 text-gray-900"
            data-oid="6u60636"
          >
            <MapPin className="w-5 h-5" data-oid="l0qz8m_" />
            <span className="font-lato" data-oid="-3egm:y">
              {t("gymPool.availableToGuests")}
            </span>
          </div>

          <Button
            asChild
            size="lg"
            className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-4 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            trackingType="booking"
            trackingLabel={t("gymPool.bookYourStay")}
            trackingLocation="Gym & Pool Section"
            data-oid="-clq6p8"
          >
            <a
              href={import.meta.env.VITE_BOOKING_URL}
              data-oid="c1tc_-t"
            >
              {t("gymPool.bookYourStay")}
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default GymPoolSection;
